import { env } from '@polleverywhere/core'
import { peFetch } from '@polleverywhere/fetch'
import store from '@polleverywhere/redux'
import { slice as appSlice } from '../slices/app'
import isMobileApp from './is_mobile_app'
import { waitFor, vizUrl, railsUrl } from './customs_util'
import URI from 'urijs'
import { userSource } from './user_source'

function calculateExpiresAt (expiresIn) {
  const expiresAt = new Date()
  expiresAt.setSeconds(expiresAt.getSeconds() + expiresIn)
  return expiresAt
}

function customsUrl (path) {
  return env.get('customs_url').pathname(path).toString()
}

function getAppRole () {
  return store.getState()[appSlice.name].role
}

function findClientId () {
  const role = getAppRole()
  const mobileApp = isMobileApp()
  if (role === 'presenter' && mobileApp) return 'com.polleverywhere.capacitor'
  if (role === 'participant' && mobileApp) return 'com.pollev.capacitor'
  if (role === 'participant') return 'com.pollev'
  if (role === 'embed') return 'com.pollev-embeds'
  if (role === 'webex') return 'com.polleverywhere.cw'
  return 'com.polleverywhere.presenter'
}

function authWithViz (token) {
  if (/participant|embed/.test(getAppRole())) return false

  return peFetch(vizUrl('/sessions/from_token'), {
    headers: { Authorization: `Bearer ${token}` }
  })
}

export class CustomsDeviceAuthorization {
  #deviceCode
  #interval
  #expiresAt
  #verificationUriComplete
  #token
  #tokenMeta
  #clientId

  get verificationUri () {
    return URI(this.#verificationUriComplete).addSearch({ usrc: userSource() }).toString()
  }

  get tokenMeta () { return this.#tokenMeta }

  get clientId () {
    if (this.#clientId) return this.#clientId
    this.#clientId = findClientId()
    return this.#clientId
  }

  async requestCode () {
    const body = new FormData()
    body.append('client_id', this.clientId)
    body.append('scope', 'authorization:login')

    const response = await fetch(customsUrl('oauth/authorize_device'), { method: 'POST', body })
    const data = await response.json()

    this.#deviceCode = data.device_code
    this.#interval = data.interval
    this.#expiresAt = calculateExpiresAt(data.expires_in)
    this.#verificationUriComplete = data.verification_uri_complete
  }

  async waitForToken () {
    if (!this.#deviceCode) { throw new Error('Missing device code') }

    while (this.#keepPolling()) {
      const data = await this.#poll()

      if (data.access_token) {
        this.#token = data.access_token
        this.#tokenMeta = data
        return this.#token
      }

      switch (data.error) {
        case 'slow_down': {
          this.#interval += 5
        } // and fallthrough to pending
        case 'authorization_pending': {
          await waitFor(this.#interval)
          break
        }
        case 'expired_token':
          throw new Error('Request expired')
        default:
          console.error('DeviceAuthorization error', data.error)
          throw new Error(`Unknown error: ${data.error}`)
      }
    }

    throw new Error('Request expired')
  }

  async buildSession () {
    if (!this.#token) { throw new Error('Missing token') }

    // Both on 'rails' and 'viz' for API and 'new viz' usage
    const sessionResponses = await Promise.all([
      authWithViz(this.#token),
      peFetch(railsUrl('/sessions/from_token'), {
        headers: { Authorization: `Bearer ${this.#token}` }
      })
    ])

    if (sessionResponses.filter(r => r && r.status !== 200).length > 0) {
      throw new Error('Failed to sync session')
    }

    // This is no longer needed as we've setup classic sessions in rails and customs
    await this.#revoke()
  }

  #keepPolling () {
    return new Date() < this.#expiresAt
  }

  async #poll () {
    const body = new FormData()
    body.append('grant_type', 'urn:ietf:params:oauth:grant-type:device_code')
    body.append('client_id', this.clientId)
    body.append('device_code', this.#deviceCode)

    const response = await fetch(customsUrl('/oauth/token'), { method: 'POST', body })
    return await response.json()
  }

  async #revoke () {
    const body = new FormData()
    body.append('client_id', this.clientId)
    body.append('token', this.#token)

    const response = await fetch(customsUrl('/oauth/revoke'), { method: 'POST', body })

    if (response.status !== 200) {
      throw new Error('Failed to revoke token')
    }
  }
}
